// src/pages/PaymentsReceivedPage.tsx
import React, { useMemo, useState } from "react";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { PaymentReceived } from "../data/mockData";
import { projectDisplayLabelPretty } from "../utils/format";
import { useTranslation } from "react-i18next";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

type Method = "Cash" | "Bank";

function IconCheck({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUndo({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 14 4 9l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function PaymentsReceivedPage() {
  const { t } = useTranslation();
  const { clients, projects, paymentsReceived, addPaymentReceived, updatePaymentReceived, deletePaymentReceived } =
    useData() as any;

  // -------------------------
  // Page controls
  // -------------------------
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // -------------------------
  // Add form
  // -------------------------
  const [projectId, setProjectId] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<Method | "">("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  // -------------------------
  // Edit state
  // -------------------------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editProjectId, setEditProjectId] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editMethod, setEditMethod] = useState<Method | "">("");
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const projectLabel = (pid: string) => {
    const p = projects.find((x: any) => x.id === pid);
    return p ? projectDisplayLabelPretty(p, clients) : pid;
  };

  // -------------------------
  // Visible rows (search + newest first)
  // -------------------------
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return [...paymentsReceived]
      .filter((pr: any) => {
        if (!q) return true;

        const p = projects.find((x: any) => x.id === pr.projectId);

        const hay = [
          pr.id,
          pr.date,
          pr.paymentMethod,
          String(pr.amount ?? ""),
          pr.notes || "",
          pr.projectId,
          p?.name || "",
          p?.address || "",
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a: any, b: any) => (a.date < b.date ? 1 : -1));
  }, [paymentsReceived, projects, search]);

  // -------------------------
  // Totals (filtered)
  // -------------------------
  const totals = useMemo(() => {
    const total = rows.reduce((s: number, r: any) => s + (Number(r.amount) || 0), 0);
    const cash = rows
      .filter((r: any) => r.paymentMethod === "Cash")
      .reduce((s: number, r: any) => s + (Number(r.amount) || 0), 0);
    const bank = rows
      .filter((r: any) => r.paymentMethod === "Bank")
      .reduce((s: number, r: any) => s + (Number(r.amount) || 0), 0);

    return { total, cash, bank };
  }, [rows]);

  // -------------------------
  // Handlers
  // -------------------------
  const resetAdd = () => {
    setProjectId("");
    setAmount("");
    setPaymentMethod("");
    setDate("");
    setNotes("");
    setFormError(null);
  };

  const handleAdd = () => {
    const amt = Number(amount);

    if (!projectId || !amount || !paymentMethod || !date) {
      setFormError("Project, Amount, Method, and Date are required.");
      return;
    }
    if (Number.isNaN(amt) || amt <= 0) {
      setFormError("Amount must be a valid number greater than 0.");
      return;
    }

    addPaymentReceived({
      id: generateId("PAY"),
      projectId,
      amount: amt,
      paymentMethod: paymentMethod as Method,
      date,
      notes: notes.trim() ? notes.trim() : undefined,
    });

    resetAdd();
  };

  const startEdit = (pr: PaymentReceived) => {
    setEditingId(pr.id);
    setEditProjectId((pr as any).projectId || "");
    setEditAmount(String((pr as any).amount ?? ""));
    setEditMethod(((pr as any).paymentMethod as Method) || "");
    setEditDate((pr as any).date || "");
    setEditNotes((pr as any).notes || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditProjectId("");
    setEditAmount("");
    setEditMethod("");
    setEditDate("");
    setEditNotes("");
  };

  const saveEdit = (pr: PaymentReceived) => {
    const amt = Number(editAmount);

    if (!editProjectId || !editAmount || !editMethod || !editDate) {
      alert(t("paymentsReceived.requiredFields"));
      return;
    }
    if (Number.isNaN(amt) || amt <= 0) {
      alert(t("paymentsReceived.amountInvalid"));
      return;
    }

    updatePaymentReceived({
      ...(pr as any),
      projectId: editProjectId,
      amount: amt,
      paymentMethod: editMethod as Method,
      date: editDate,
      notes: editNotes.trim() ? editNotes.trim() : undefined,
    });

    cancelEdit();
  };

  const doDelete = (pr: PaymentReceived) => {
    const ok = window.confirm(t("paymentsReceived.deleteConfirm"));
    if (!ok) return;

    deletePaymentReceived((pr as any).id);
    if (editingId === (pr as any).id) cancelEdit();
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="container">
      {/* Page header (title left, search top-right) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>Payments Received</h1>
          <div className="pageNote">
            {t("paymentsReceived.pageNote")}
          </div>
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label style={{ display: "block", fontSize: 12, color: "var(--pageMuted)" }}>{t("common.search")}</label>
          <input
            className="input"
            placeholder={t("paymentsReceived.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* ADD PAYMENT */}
      <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
        <div className="cardHeader">
          <h3 className="cardTitle">{t("paymentsReceived.addTitle")}</h3>
          <div className="cardSub">
            <span className="reqStar">*</span> {t("common.required")}
          </div>
        </div>

        <div className="formGrid">
          {/* Row 1: Project full width */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">
              {t("common.project")} <span className="reqStar">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            >
              <option value="">{t("paymentsReceived.selectProject")}</option>
              {projects.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {projectLabel(p.id)}
                </option>
              ))}
            </select>
          </div>

          {/* Row 2: Amount + Method + Date (keeps it tight like other pages) */}
          <div className="field">
            <label className="label">
              {t("paymentsReceived.amountLabel")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            />
          </div>

          <div className="field">
            <label className="label">
              {t("paymentsReceived.methodLabel")} <span className="reqStar">*</span>
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e.target.value as any);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            >
              <option value="">{t("paymentsReceived.selectMethod")}</option>
              <option value="Cash">{t("common.cash")}</option>
              <option value="Bank">{t("common.bank")}</option>
            </select>
          </div>

          <div className="field">
            <label className="label">
              {t("paymentsReceived.dateLabel")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                if (formError) setFormError(null);
              }}
              style={{ width: "100%" }}
            />
          </div>

          {/* Row 3: Notes full width */}
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label className="label">{t("common.notes")}</label>
            <textarea
              className="input"
              placeholder={t("paymentsReceived.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ width: "100%", minHeight: 86, resize: "vertical" }}
            />
          </div>
        </div>

        {formError && (
          <div className="formError" role="alert">
            {formError}
          </div>
        )}

        <div className="formActions">
          <button className="btn" onClick={handleAdd} type="button">{t("paymentsReceived.addBtn")}</button>
          <button className="btn btnSecondary" onClick={resetAdd} type="button">{t("common.clear")}</button>
        </div>
      </div>

      {/* TOTALS (filtered) — between Add and Ledger, single line */}
      <div className="card" style={{ padding: 12, marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 1000 }}>
            Totals (filtered)
            <span className="badge" style={{ marginLeft: 8 }} title={t("paymentsReceived.ledgerTip")}>
              {rows.length} rows
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              fontSize: 13,
            }}
          >
            <span style={{ color: "var(--muted)" }}>
              {t("paymentsReceived.totalLabel")} <b className="numGood">{money(totals.total)}</b>
            </span>
            <span style={{ color: "var(--muted)" }}>
              {t("paymentsReceived.cashLabel")} <b className="numWarn">{money(totals.cash)}</b>
            </span>
            <span style={{ color: "var(--muted)" }}>
              {t("paymentsReceived.bankLabel")} <b className="numWarn">{money(totals.bank)}</b>
            </span>
          </div>
        </div>
      </div>

      {/* LEDGER */}
      <div className="card">
        <div className="cardHeader">
          <div>
            <h3 className="cardTitle">{t("paymentsReceived.ledgerTitle")}</h3>
            <div className="cardSub">{rows.length} rows • newest first</div>
          </div>

          <span className="badge" title={t("paymentsReceived.ledgerTip2")}>
            Ledger
          </span>
        </div>

        <div className="tableWrap tableWrapY">
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ width: 110 }}>ID</th>
                <th>{t("paymentsReceived.colProject")}</th>
                <th style={{ width: 130, textAlign: "right" }}>{t("common.amount")}</th>
                <th style={{ width: 120 }}>{t("common.method")}</th>
                <th style={{ width: 130 }}>{t("common.date")}</th>
                <th>{t("paymentsReceived.colNotes")}</th>
                <th style={{ width: 140 }}>{t("common.actions")}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((pr: any) => {
                const isEditing = editingId === pr.id;

                return (
                  <tr key={pr.id}>
                    <td style={{ fontWeight: 900 }}>{pr.id}</td>

                    <td>
                      {isEditing ? (
                        <select
                          value={editProjectId}
                          onChange={(e) => setEditProjectId(e.target.value)}
                          style={{ width: "100%" }}
                        >
                          <option value="">{t("paymentsReceived.selectProject")}</option>
                          {projects.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {projectLabel(p.id)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="cellClamp" title={projectLabel(pr.projectId)}>
                          {projectLabel(pr.projectId)}
                        </span>
                      )}
                    </td>

                    <td style={{ textAlign: "right" }}>
                      {isEditing ? (
                        <input
                          className="input"
                          type="number"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          style={{ width: 120 }}
                        />
                      ) : (
                        <span className="numGood">{money(pr.amount)}</span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <select
                          value={editMethod}
                          onChange={(e) => setEditMethod(e.target.value as any)}
                          style={{ width: "100%" }}
                        >
                          <option value="">{t("paymentsReceived.methodLabel")}</option>
                          <option value="Cash">{t("common.cash")}</option>
                          <option value="Bank">{t("common.bank")}</option>
                        </select>
                      ) : (
                        <span className="badge">{pr.paymentMethod}</span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      ) : (
                        pr.date
                      )}
                    </td>

                    <td style={{ minWidth: 220 }}>
                      {isEditing ? (
                        <input
                          className="input"
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          style={{ width: "100%" }}
                          placeholder={t("paymentsReceived.optionalNotes")}
                        />
                      ) : (
                        <span className="cellClamp" title={pr.notes || ""}>
                          {pr.notes || "-"}
                        </span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <div className="actionIcons">
                          <button
                            className="iconActionBtn primary"
                            onClick={() => saveEdit(pr)}
                            type="button"
                            title={t("paymentsReceived.saveBtn")}
                          >
                            <IconCheck />
                          </button>
                          <button
                            className="iconActionBtn"
                            onClick={cancelEdit}
                            type="button"
                            title={t("paymentsReceived.cancelBtn")}
                          >
                            <IconUndo />
                          </button>
                        </div>
                      ) : (
                        <div className="actionIcons">
                          <button
                            className="iconActionBtn primary"
                            onClick={() => startEdit(pr)}
                            type="button"
                            title={t("paymentsReceived.editBtn")}
                          >
                            <IconPencil />
                          </button>
                          <button
                            className="iconActionBtn danger"
                            onClick={() => doDelete(pr)}
                            type="button"
                            title={t("paymentsReceived.deleteBtn")}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                    {t("paymentsReceived.noPayments")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pageNote" style={{ marginTop: 10 }}>
          {t("paymentsReceived.tipNotes")}
        </div>
      </div>
    </div>
  );
}

